import myaxios from "@/lib/axios";
import { components } from "@/lib/openapi";
import { ReportProvider } from "@/stores/ReportContext";
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

type SessionDataType = components["schemas"]["UserSchema"];

// Extend the UserSchema to include derived properties
export type ExtendedSessionDataType = SessionDataType & {
  isBrand: boolean;
  isAirport: boolean;
  isRetailAll: boolean;
};

export type SessionContextType = {
  sessionData: ExtendedSessionDataType;
  refetchSessionData: () => Promise<void>;
};

const SessionDataContext = createContext<SessionContextType | null>(null);

function getCookie(name: string): string | null {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const SessionDataProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const navigate = useNavigate();

  const [sessionData, setSessionData] =
    useState<ExtendedSessionDataType | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Function to create the extended session data
  const createExtendedSessionData = (
    data: SessionDataType
  ): ExtendedSessionDataType => {
    return {
      ...data,
      isBrand: data.session_type === "brand",
      isAirport: data.session_type === "airport",
      isRetailAll: data.session_type === "retail_all",  // NEW

    };
  };

  // Function to load session data
  const loadSessionData = async () => {
    try {
      // Check if auth token exists
      const authToken = getCookie("auth_token");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const response = await myaxios.get("/api/session/data");
      const sessionDataResponse = createExtendedSessionData(response.data);

      setSessionData(sessionDataResponse);

      if (sessionDataResponse.force_password_reset && window.location.pathname !== "/reset") {
        navigate("/reset");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch session data")
      );
      console.error("Failed to fetch session data:", err);
    }
  };

  // Fetch the session data on mount
  useEffect(() => {
    loadSessionData();
  }, []);

  if (!sessionData) {
    return null;
  }

  const contextValue: SessionContextType = {
    sessionData,
    refetchSessionData: loadSessionData,
  };

  return (
    <SessionDataContext.Provider value={contextValue}>
      <ReportProvider>{children}</ReportProvider>
    </SessionDataContext.Provider>
  );
};

// Custom hook to use the sessionData context
export const useSessionData = () => {
  const context = useContext(SessionDataContext);
  if (context === null) {
    throw new Error("useSessionData must be used within a SessionDataProvider");
  }
  return context.sessionData;
};

// Custom hook to get refetch function
export const useSessionRefetch = () => {
  const context = useContext(SessionDataContext);
  if (context === null) {
    throw new Error(
      "useSessionRefetch must be used within a SessionDataProvider"
    );
  }
  return context.refetchSessionData;
};

export default SessionDataProvider;
